import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    IApp03Workflow, EntityValidations, ISpecificEntityFormProps, App03Workflow, App03WorkflowUser, IApp03WorkflowStage,
    CustomField
} from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { CrDropdown } from '../cr/CrDropdown';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { getWorkflowTypeString, WorkflowRefColumn, WorkflowType } from '../../refData/WorkflowType';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';
import { App03Frameworks, App03WorkflowParentTypes } from '../../types/App03Workflow';
import { App03TaskTimeOptionKanban, App03TaskTimeOptionScrum } from '../../types/App03Task';
import { getPlanningStrategyOptions } from './TaskFormPage';

class WorkflowValidations extends EntityValidations {
    public Owners: string = null;
    public RefPrefix: string = null;
    public EpicRefPrefix: string = null;
    public ParentType: string = null;
    public Portfolio: string = null;
    public Project: string = null;
    public Template: string = null;
}

interface IWorkflowFormProps extends ISpecificEntityFormProps {
    frameworkID: App03Frameworks;
}

export const WorkflowForm = (props: IWorkflowFormProps): React.ReactElement => {
    const { disableGroupManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { customFieldDfns, entityStatuses, app03Portfolios, app03Projects, app03Templates, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => app03Portfolios(), [app03Portfolios]);
    useMemo(() => app03Projects(), [app03Projects]);
    useMemo(() => app03Templates(), [app03Templates]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => allUsers(), [allUsers]);

    const [workflowStages, setWorkflowStages] = useState<IApp03WorkflowStage[]>([]);

    useEffect(() => {
        const fetchStages = async () => {
            if (props.entityId > 0) {
                try {
                    const stages = await dataServices.app03WorkflowStageService.readWorkflowStages(props.entityId);
                    setWorkflowStages(stages);
                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            }
        };

        fetchStages();
    }, [props.entityId]);


    const isInvalidId = (value: any): boolean =>
        value === null ||
        value === undefined ||
        value === "" ||
        isNaN(Number(value));


    const validateEntity = (workflow: IApp03Workflow): Promise<WorkflowValidations> => {
        const errors = new WorkflowValidations();
        //console.log('on validation', workflow);

        if (workflow.Title === null || workflow.Title === '') {
            errors.Title = `WorkStream name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (workflow.ParentTypeID === null) {
            errors.ParentType = `Parent Type is required`;
            errors.Valid = false;
        }

        else {
            errors.ParentType = null;
        }
        if (isInvalidId(workflow.PPProjectID)) {
            if (workflow.ParentTypeID === App03WorkflowParentTypes.Portfolio && isInvalidId(workflow.App03PortfolioID)) {
                errors.Portfolio = "Portfolio is required";
                errors.Valid = false;
            } else {
                errors.Portfolio = null;
            }

            if (workflow.ParentTypeID === App03WorkflowParentTypes.Project && isInvalidId(workflow.App03ProjectID)) {
                errors.Project = "Project is required";
                errors.Valid = false;
            } else {
                errors.Project = null;
            }
        }

        if (isInvalidId(workflow.App03TemplateID)) {
            errors.Template = `Template is required`;
            errors.Valid = false;
        } else {
            errors.Template = null;
        }

        if (workflow.App03WorkflowUsers === undefined || workflow.App03WorkflowUsers === null || workflow.App03WorkflowUsers.filter(x => x.IsAdmin === true).length < 1) {
            errors.Owners = `At least one owner is required`;
            errors.Valid = false;
        }
        else {
            errors.Owners = null;
        }
        if (workflow.RefColumnID === 1 && (workflow.RefPrefix === null || workflow.RefPrefix === '')) {
            errors.RefPrefix = "Ref Prefix is required"
            errors.Valid = false;
        }
        else {
            errors.RefPrefix = null;
        }

        if (workflow.EpicRefPrefix === null || workflow.EpicRefPrefix === '') {
            errors.EpicRefPrefix = "Epic Ref Prefix is required"
            errors.Valid = false;
        }
        else {
            errors.EpicRefPrefix = null;
        }


        return Promise.resolve(errors);
    };

    const allowedTypes =
        props.entityId > 0
            ? (Object.values(WorkflowType).filter(v => typeof v === "number") as WorkflowType[])
            : [WorkflowType.General, WorkflowType.StudyPlanner];

    const workflowTypesArray = allowedTypes.map(type => ({
        ID: type,
        Title: getWorkflowTypeString(type)
    }));


    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
                'All Delivery Apps - WorkStreams',
            ]
        );
    }, [lookupData.CustomFieldDfns]);


    const workflowRefColumnArray = Object.keys(WorkflowRefColumn)
        .filter(key => !isNaN(Number(WorkflowRefColumn[key])))
        .map(key => ({ ID: WorkflowRefColumn[key], Title: key }));

    const taskTimeOptions =
        props.frameworkID === App03Frameworks.Scrum
            ? [
                { key: App03TaskTimeOptionScrum.UseStoryPoints, text: "Use Story Points" },
                { key: App03TaskTimeOptionScrum.UseHours, text: "Use Hours" },
                { key: App03TaskTimeOptionScrum.UseDays, text: "Use Days" },
            ]
            : [
                { key: App03TaskTimeOptionKanban.RequiredInDifficultyPoints, text: "Required in Difficulty Points" },
                { key: App03TaskTimeOptionKanban.RequiredInHours, text: "Required in Hours" },
                { key: App03TaskTimeOptionKanban.RequiredInDays, text: "Required in Days" },
            ];


    return (
        <EntityForm<IApp03Workflow, WorkflowValidations>
            {...props}
            entityName='Workstream'
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: wf, ValidationErrors: errors } = formState;
                return (
                    <div>
                        <CrTextField
                            label="Name"
                            maxLength={100}
                            className={styles.formField}
                            required={true}
                            value={wf.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />

                        {!(props.showEntityDescription1 === true) && props.frameworkID !== App03Frameworks.Kanban &&
                            <CrDropdown
                                label='Parent Type'
                                required={true}
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions([
                                    { ID: App03WorkflowParentTypes.Portfolio, Title: 'Portfolio' },
                                    { ID: App03WorkflowParentTypes.Project, Title: 'Project' },
                                ])}
                                selectedKey={wf.ParentTypeID}
                                onChange={(_, v) => changeHandlers.changeDropdown(v, 'ParentTypeID')}
                                errorMessage={errors.ParentType}
                            />
                        }

                        {!(props.showEntityDescription1 === true || (wf.PPProjectID > 0)) && wf.ParentTypeID === App03WorkflowParentTypes.Portfolio && (
                            <CrDropdown
                                label="Portfolio"
                                required={true}
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(
                                    (lookupData.App03Portfolios ?? []).filter(
                                        p => Number(p.FrameworkID) === Number(props.frameworkID)
                                    )
                                )}
                                selectedKey={wf.App03PortfolioID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'App03PortfolioID')}
                                errorMessage={errors.Portfolio}
                            />
                        )}

                        {!(props.showEntityDescription1 === true || (wf.PPProjectID > 0)) && wf.ParentTypeID === App03WorkflowParentTypes.Project && (
                            <CrDropdown
                                label="Project"
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(
                                    (lookupData.App03Projects ?? []).filter(
                                        p => Number(p.FrameworkID) === Number(props.frameworkID)
                                    )
                                )}
                                selectedKey={wf.App03ProjectID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'App03ProjectID')}
                                errorMessage={errors.Project}
                            />
                        )}

                        <CrDropdown
                            label="Template"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(
                                (lookupData.App03Templates ?? []).filter(
                                    t => Number(t.FrameworkID) === Number(props.frameworkID)
                                )
                            )}
                            selectedKey={wf.App03TemplateID}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'App03TemplateID')}
                            required={true}
                            errorMessage={errors.Template}
                        />

                        {(isInvalidId(wf.PPProjectID) || wf.ID > 0) && <CrDropdown
                            label="Type"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(workflowTypesArray)}
                            selectedKey={wf.WorkflowTypeID}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'WorkflowTypeID')}
                            disabled={wf.ID > 0}
                        />}

                        <CrTextField
                            label="Description"
                            multiline
                            rows={6}
                            maxLength={10000}
                            className={styles.formField}
                            value={wf.Description}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Description')}

                        />

                        <CrUserPicker
                            label="Lead Owner"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            selectedUsers={wf.LeadOwnerID && [wf.LeadOwnerID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'LeadOwnerID')}
                        />

                        <CrUserPicker
                            label="Owner(s)"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            itemLimit={5}
                            required={true}
                            errorMessage={errors.Owners}
                            selectedUsers={wf.App03WorkflowUsers?.filter(u => u.IsAdmin === true).map(u => u.UserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'App03WorkflowUsers', new App03WorkflowUser(true), 'UserID')}
                        />

                        <CrUserPicker
                            label="Assigned to Default"
                            className={styles.formField}
                            users={lookupData.Users?.All}
                            selectedUsers={wf.AssignedToDefaultUserID && [wf.AssignedToDefaultUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'AssignedToDefaultUserID')}
                        />

                        {props.entityId > 0 && workflowStages.length > 0 &&
                            <CrDropdown
                                label="New task stage"
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(workflowStages)}
                                selectedKey={wf.NewTaskDefaultStageID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'NewTaskDefaultStageID')}
                            />
                        }

                        <CrDropdown
                            label="Task estimation unit"
                            className={styles.formField}
                            options={taskTimeOptions}
                            selectedKey={wf.TaskTimeOption}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'TaskTimeOption')}
                        />

                        <CrDropdown
                            label="Default Planning Strategy"
                            className={styles.formField}
                            options={getPlanningStrategyOptions(props.frameworkID)}
                            selectedKey={wf.DefaultPlanningStrategy}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'DefaultPlanningStrategy')}
                        />

                        <CrDropdown
                            label="Ref Column"
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(workflowRefColumnArray)}
                            selectedKey={wf.RefColumnID}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'RefColumnID')}
                            disabled={true}
                        />

                        <CrTextField
                            label="Task Ref Prefix"
                            maxLength={5}
                            className={styles.formField}
                            required={true}
                            value={wf.RefPrefix}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'RefPrefix')}
                            errorMessage={errors.RefPrefix}
                        />

                        <CrTextField
                            label="Epic Ref Prefix"
                            maxLength={5}
                            className={styles.formField}
                            required={true}
                            value={wf.EpicRefPrefix}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'EpicRefPrefix')}
                            errorMessage={errors.EpicRefPrefix}
                        />

                        <CrCustomFields
                            label="Additional Information"
                            className={styles.formField}
                            customFieldDfns={filteredCustomFieldDfns}
                            selectedItems={LookupService.customFieldsToDropdownWithText(wf.CustomFields)}
                            onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                        />

                        <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableGroupManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={wf.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        />

                    </div>
                );
            }}
            loadEntity={id => dataServices.app03WorkflowService.read(id, true, true)}
            loadNewEntity={() => new App03Workflow(props.frameworkID)}
            loadEntityValidations={() => new WorkflowValidations()}
            onValidateEntity={validateEntity}
            onCreate={w => dataServices.app03WorkflowService.create(w)}
            onUpdate={g => dataServices.app03WorkflowService.update(g.ID, g)}
            parentEntities={dataServices.app03WorkflowService.parentEntities}
            onAfterCreate={async (e) => {
                if (e.PPProjectID > 0) {
                    //console.log("on after create", e);
                    const updatedEntity = {
                        ...e,
                        InternalMsg: "FromPPWSAddChilds"
                    };

                    await dataServices.app03WorkflowService.updatePut(updatedEntity.ID, updatedEntity);
                    return updatedEntity;
                }
                return Promise.resolve(e);
            }}
            onAfterUpdate2={async (e) => {
                if (e.PPProjectID > 0) {
                    //console.log("on after update", e);
                    const updatedEntity = {
                        ...e,
                        InternalMsg: "FromPPWSAddChilds"
                    };

                    await dataServices.app03WorkflowService.updatePut(updatedEntity.ID, updatedEntity);
                    return updatedEntity;
                }
                return Promise.resolve(e);
            }}

            childEntities={[
                {
                    ObjectParentProperty: 'App03WorkflowUsers',
                    ParentIdProperty: 'App03WorkflowID',
                    ChildIdProperty: 'UserID',
                    ChildService: dataServices.app03WorkflowUserService
                },
                {
                    ObjectParentProperty: 'CustomFields',
                    ParentIdProperty: 'App03WorkflowID',
                    ChildIdProperty: 'CustomFieldDefnID',
                    ChildService: dataServices.customFieldService
                }
            ]}
        />
    );
};
